<template>
  <div class="cat-serie">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-serie-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${series.length} series`"
          @new="addSerie"
          @refresh="refreshData"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando series...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!series || series.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay series registradas. Haga clic en 'Nuevo' para agregar una.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && series && series.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="series"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar series..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot personalizado para la columna EsActivo -->
            <template #cell-EsActivo="{ value }">
              <span v-if="value" class="badge bg-success">Sí</span>
              <span v-else class="badge bg-secondary">No</span>
            </template>
            
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editSerie(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar serie"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar serie"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Serie -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="serieModal" 
      tabindex="-1" 
      aria-labelledby="serieModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="serieModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Serie' : 'Nuevo Serie' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatSerieForm
              :serie="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatSerieService from '../controllers/CatSerieController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatSerieForm from '../components/CatSerieForm.vue';

export default {
  name: 'CatSerieView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatSerieForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl();

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    };
  },
  data() {
    return {
      service: null,
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: null,
        ObjetoId: null,
        Nombre: '',
        SiguienteFolio: 0,
        ClaveErp: '',
        EsActivo: true,
        TipoDocumento: '',
        Serie: '',
        FolioInicial: 0,
        FolioFinal: 0,
        Estatus: ''
      },
      // Columnas del data grid
      dataGridColumns: [
        { key: 'Clave', label: 'Clave', sortable: true, width: '100px' },
        { key: 'EmpresaId', label: 'Empresa ID', sortable: true, width: '100px' },
        { key: 'ObjetoId', label: 'Objeto ID', sortable: true, width: '100px' },
        { key: 'Nombre', label: 'Nombre', sortable: true },
        { key: 'SiguienteFolio', label: 'Siguiente Folio', sortable: true, width: '120px' },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true, width: '100px' },
        { key: 'EsActivo', label: 'Estado', sortable: true, width: '100px' },
        { key: 'TipoDocumento', label: 'Tipo Documento', sortable: true, width: '120px' },
        { key: 'Serie', label: 'Serie', sortable: true, width: '100px' },
        { key: 'FolioInicial', label: 'Folio Inicial', sortable: true, width: '120px' },
        { key: 'FolioFinal', label: 'Folio Final', sortable: true, width: '120px' },
        { key: 'Estatus', label: 'Estatus', sortable: true, width: '100px' },
        { key: 'actions', label: 'Acciones', sortable: false, width: '120px' }
      ]
    };
  },
  created() {
    this.service = new CatSerieService();
    this.service.initialize();
  },
  computed: {
    // Computed properties para el servicio
    series() {
      return this.service?.series || [];
    },
    loading() {
      return this.service?.loading || false;
    },
    error() {
      return this.service?.error || null;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    }
  },
  methods: {
    // Métodos de la toolbar
    addSerie() {
      this.isEditing = false;
      this.openModal();
    },
    refreshData() {
      if (this.service) {
        this.service.recargar();
      }
    },
    async downloadBulk() {
      try {
        if (this.service) {
          await this.service.downloadBulk();
          this.$toast.success('Descarga iniciada correctamente');
        }
      } catch (error) {
        this.$toast.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del data grid
    editSerie(serie) {
      this.isEditing = true;
      this.formData = { ...serie };
      this.openModal();
    },
    async confirmDelete(clave) {
      const confirmed = await this.$alert.confirm(
        '¿Está seguro de que desea eliminar esta serie?',
        'Confirmar eliminación'
      );
      
      if (confirmed) {
        try {
          if (this.service) {
            await this.service.borrar(clave);
            this.$toast.success('Serie eliminada exitosamente');
          }
        } catch (error) {
          this.$toast.error('No se pudo eliminar la serie. Por favor, intente nuevamente.');
        }
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: this.service?.selectedEmpresa || null,
        ObjetoId: null,
        Nombre: '',
        SiguienteFolio: 0,
        ClaveErp: '',
        EsActivo: true,
        TipoDocumento: '',
        Serie: '',
        FolioInicial: 0,
        FolioFinal: 0,
        Estatus: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        if (this.service) {
          const response = await this.service.guardar(formData);
          
          if (response && response.success) {
            this.$toast.success(response.message || 'Serie guardada exitosamente');
            this.closeModal();
            this.service.recargar();
          } else {
            this.$toast.error(response.message || 'No se pudo guardar la serie. Por favor, intente nuevamente.');
          }
        }
      } catch (error) {
        console.error('Error al guardar serie:', error);
        this.$toast.error('No se pudo guardar la serie. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-serie-content {
  padding: 0;
}

.toolbar-container {
  margin-bottom: 1.5rem;
}

.page-content {
  padding: 1.25rem 1rem 1rem 1rem;
  margin-top: .5rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
